import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { fmt } from "@/lib/units";
import { Ruler, Plus, Trash2, CheckCircle2, AlertTriangle } from "lucide-react";

export default function LimitationsTab({ briefing, setBriefing, aircraft }) {
  const items = briefing.limitations || [];

  const add = () =>
    setBriefing((b) => ({
      ...b,
      limitations: [
        ...(b.limitations || []),
        {
          airport: "",
          tora: 0,
          lda: 0,
          requiredTakeoff: aircraft?.takeoffDist || 0,
          requiredLanding: aircraft?.landingDist || 0,
        },
      ],
    }));
  const setItem = (i, key, v) =>
    setBriefing((b) => {
      const limitations = [...b.limitations];
      limitations[i] = { ...limitations[i], [key]: key === "airport" ? v : Number(v) };
      return { ...b, limitations };
    });
  const remove = (i) => setBriefing((b) => ({ ...b, limitations: b.limitations.filter((_, x) => x !== i) }));

  return (
    <div className="space-y-5">
      <div className="glass rounded-2xl p-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-display text-sm font-semibold uppercase tracking-wide text-cyan-400">
            <Ruler className="h-4 w-4" /> Limitations opérationnelles (distances)
          </h3>
          <Button onClick={add} size="sm" data-testid="add-limitation-button"
            className="bg-cyan-500 font-semibold text-[#070C18] hover:bg-cyan-400">
            <Plus className="mr-1 h-3.5 w-3.5" /> Terrain
          </Button>
        </div>
        <p className="mb-4 text-xs text-slate-500">
          Base avion : décollage {aircraft?.takeoffDist || 0} m · atterrissage {aircraft?.landingDist || 0} m. Ajustez le requis selon altitude/température/piste.
        </p>

        {items.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-700 py-10 text-center text-sm text-slate-500">
            Aucun terrain. Ajoutez le terrain de départ et d'arrivée.
          </div>
        ) : (
          <div className="space-y-4">
            {items.map((it, i) => {
              const toOk = Number(it.tora) >= Number(it.requiredTakeoff);
              const ldOk = Number(it.lda) >= Number(it.requiredLanding);
              return (
                <div key={i} data-testid={`limitation-row-${i}`} className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
                  <div className="mb-3 flex items-center gap-3">
                    <Input value={it.airport} onChange={(e) => setItem(i, "airport", e.target.value.toUpperCase())}
                      placeholder="OACI (LFPN)" className="h-9 w-40 border-slate-700 bg-slate-900/60 font-mono-data text-slate-100" data-testid={`lim-airport-${i}`} />
                    <button onClick={() => remove(i)} className="ml-auto rounded-lg p-2 text-slate-400 hover:bg-rose-500/10 hover:text-rose-300">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider text-slate-500">TORA dispo (m)</Label>
                      <Input type="number" value={it.tora} onChange={(e) => setItem(i, "tora", e.target.value)}
                        className="mt-1 h-9 border-slate-700 bg-slate-900/60 font-mono-data text-slate-100" data-testid={`lim-tora-${i}`} />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider text-slate-500">Déco requis (m)</Label>
                      <Input type="number" value={it.requiredTakeoff} onChange={(e) => setItem(i, "requiredTakeoff", e.target.value)}
                        className="mt-1 h-9 border-slate-700 bg-slate-900/60 font-mono-data text-slate-100" />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider text-slate-500">LDA dispo (m)</Label>
                      <Input type="number" value={it.lda} onChange={(e) => setItem(i, "lda", e.target.value)}
                        className="mt-1 h-9 border-slate-700 bg-slate-900/60 font-mono-data text-slate-100" data-testid={`lim-lda-${i}`} />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider text-slate-500">Atterr. requis (m)</Label>
                      <Input type="number" value={it.requiredLanding} onChange={(e) => setItem(i, "requiredLanding", e.target.value)}
                        className="mt-1 h-9 border-slate-700 bg-slate-900/60 font-mono-data text-slate-100" />
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${toOk ? "bg-emerald-500/10 text-emerald-300" : "bg-rose-500/10 text-rose-300"}`}>
                      {toOk ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
                      Décollage {toOk ? "OK" : "INSUFFISANT"} (marge {fmt(Number(it.tora) - Number(it.requiredTakeoff))} m)
                    </span>
                    <span className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${ldOk ? "bg-emerald-500/10 text-emerald-300" : "bg-rose-500/10 text-rose-300"}`}>
                      {ldOk ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
                      Atterrissage {ldOk ? "OK" : "INSUFFISANT"} (marge {fmt(Number(it.lda) - Number(it.requiredLanding))} m)
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
